import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, UserCircle, UsersThree, Package, Buildings } from '@phosphor-icons/react';
import ListSearch from '../../../components/ListSearch';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import {
  getAssetTeam, listProductClients, updateProductClient, deleteProductClient, updateTeamMemberClients,
} from '../api';
import { ROLE_BADGE, ModalShell, Field } from '../ui';

/**
 * One product/team — member roster, and (when tied to a product) its Clients.
 * Clicking a member opens their asset page; clicking a client opens the
 * client detail page (client-scoped team members + infrastructure).
 */
function TeamDetailPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('members');

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientModal, setClientModal] = useState(null); // null | client object being edited
  const [clientForm, setClientForm] = useState({ name: '' });
  const [clientSaving, setClientSaving] = useState(false);
  const [clientError, setClientError] = useState(null);

  const [assigningMemberId, setAssigningMemberId] = useState(null);
  const [memberClientError, setMemberClientError] = useState(null);
  const [editingClientFor, setEditingClientFor] = useState(null); // user_id whose client picker is open
  const [editingClientIds, setEditingClientIds] = useState([]); // in-progress checkbox selection

  useEffect(() => {
    setLoading(true);
    setError(null);
    setActiveTab('members');
    getAssetTeam(teamId)
      .then(setTeam)
      .catch((e) => setError(e.response?.data?.detail || 'Could not load team'))
      .finally(() => setLoading(false));
  }, [teamId]);

  const refreshClients = useCallback(() => {
    if (!team?.product_id) return;
    setClientsLoading(true);
    listProductClients(team.product_id)
      .then(setClients)
      .catch((e) => console.error('Failed to fetch clients:', e))
      .finally(() => setClientsLoading(false));
  }, [team]);

  useEffect(() => {
    if (team?.product_id) refreshClients();
  }, [team, refreshClients]);

  const openClientEditor = (member) => {
    setMemberClientError(null);
    setEditingClientFor(member.user_id);
    setEditingClientIds(member.client_ids || []);
  };

  const toggleEditingClientId = (clientId) => {
    setEditingClientIds((prev) => (
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    ));
  };

  const saveMemberClients = async (userId) => {
    setAssigningMemberId(userId);
    setMemberClientError(null);
    try {
      await updateTeamMemberClients(team.product_id, userId, editingClientIds);
      const refreshed = await getAssetTeam(teamId);
      setTeam(refreshed);
      setEditingClientFor(null);
    } catch (err) {
      setMemberClientError(err.response?.data?.detail || 'Failed to reassign clients');
    } finally {
      setAssigningMemberId(null);
    }
  };

  const q = query.trim().toLowerCase();
  const members = !team ? [] : !q ? team.members : team.members.filter((m) => (
    (m.name || '').toLowerCase().includes(q)
    || (m.email || '').toLowerCase().includes(q)
    || (m.designation || '').toLowerCase().includes(q)
  ));

  const openClientModal = (client) => {
    setClientError(null);
    setClientModal(client);
    setClientForm({ name: client.name });
  };

  const saveClient = async (e) => {
    e.preventDefault();
    setClientSaving(true);
    setClientError(null);
    try {
      await updateProductClient(clientModal.client_id, clientForm);
      setClientModal(null);
      refreshClients();
    } catch (err) {
      setClientError(err.response?.data?.detail || 'Failed to save client');
    } finally {
      setClientSaving(false);
    }
  };

  const removeClient = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"?`)) return;
    try {
      await deleteProductClient(client.client_id);
      refreshClients();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  return (
    <div data-testid="asset-team-detail">
      <Link to="/dashboard/assets/companies" style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
        color: '#002FA7', textDecoration: 'none', marginBottom: '8px', fontWeight: '600',
      }}>
        <ArrowLeft size={14} /> Companies
      </Link>

      {loading ? (
        <SkeletonBlock />
      ) : error ? (
        <EmptyState icon={UsersThree} title="Team unavailable" description={error} />
      ) : (
        <>
          <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <p style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{team.name}</p>
              {!team.active && (
                <span style={{ fontSize: '11px', color: '#9CA3AF' }}>inactive</span>
              )}
            </div>
            {team.description && (
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>{team.description}</p>
            )}
            <div style={{ display: 'flex', gap: '14px', fontSize: '12px', color: '#6B7280' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <UsersThree size={13} /> {team.member_count} member{team.member_count === 1 ? '' : 's'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Package size={13} /> {team.assets_assigned} asset{team.assets_assigned === 1 ? '' : 's'} assigned
              </span>
            </div>
          </div>

          {team.product_id && (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', borderBottom: '1px solid #E5E7EB' }}>
              {[
                { id: 'members', label: 'Members', icon: UsersThree },
                { id: 'clients', label: 'Clients', icon: Buildings },
              ].map((t) => (
                <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                    background: 'none', border: 'none', borderBottom: activeTab === t.id ? '2px solid #002FA7' : '2px solid transparent',
                    color: activeTab === t.id ? '#002FA7' : '#6B7280', fontWeight: activeTab === t.id ? '600' : '500',
                    fontSize: '12px', cursor: 'pointer',
                  }}>
                  <t.icon size={15} /> {t.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'members' && (
            <>
              <ListSearch
                value={query}
                onChange={setQuery}
                placeholder="Search team members…"
                resultCount={members.length}
                totalCount={team.members.length}
              />

              {memberClientError && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '6px' }}>{memberClientError}</p>}

              {members.length === 0 ? (
                <EmptyState
                  icon={UsersThree}
                  title="No team members"
                  description={query ? 'Try a different search.' : 'Members added in Product Management will appear here.'}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {members.map((m) => {
                    const assignedClients = (m.client_ids || [])
                      .map((cid) => clients.find((c) => c.client_id === cid))
                      .filter(Boolean);
                    const isEditing = editingClientFor === m.user_id;
                    return (
                      <div key={m.user_id} data-testid="team-member-row"
                        style={{
                          display: 'flex', alignItems: isEditing ? 'flex-start' : 'center', gap: '10px', width: '100%',
                          padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: 'white',
                        }}>
                        {m.picture ? (
                          <img src={m.picture} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <UserCircle size={30} color="#9CA3AF" />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <button type="button"
                            onClick={() => navigate(`/dashboard/assets/teams/${teamId}/members/${m.user_id}`)}
                            style={{
                              display: 'block', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                              fontSize: '12px', fontWeight: '600', color: '#002FA7',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                            }}>
                            {m.name}
                          </button>
                          <p style={{ fontSize: '11px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.designation || m.email}
                          </p>
                          {team.product_id && (
                            <div style={{ marginTop: '4px' }}>
                              {isEditing ? (
                                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px' }}>
                                  {clients.length === 0 ? (
                                    <p style={{ fontSize: '11px', color: '#6B7280' }}>No clients on this product yet.</p>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                                      {clients.map((c) => (
                                        <label key={c.client_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                                          <input type="checkbox"
                                            checked={editingClientIds.includes(c.client_id)}
                                            onChange={() => toggleEditingClientId(c.client_id)} />
                                          {c.name}
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="button" onClick={() => saveMemberClients(m.user_id)}
                                      disabled={assigningMemberId === m.user_id}
                                      style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: '#002FA7', fontWeight: '600', padding: 0 }}>
                                      {assigningMemberId === m.user_id ? 'Saving…' : 'Save'}
                                    </button>
                                    <button type="button" onClick={() => setEditingClientFor(null)}
                                      disabled={assigningMemberId === m.user_id}
                                      style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0 }}>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button type="button" onClick={() => openClientEditor(m)}
                                  style={{
                                    fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                    color: assignedClients.length ? '#374151' : '#002FA7', fontWeight: assignedClients.length ? '500' : '600',
                                  }}>
                                  {assignedClients.length ? assignedClients.map((c) => c.name).join(', ') : '+ Add client'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="badge" style={{ background: ROLE_BADGE[m.org_role] || '#F3F4F6', fontSize: '11px', textTransform: 'capitalize' }}>
                          {m.org_role}
                        </span>
                        <span className="badge" style={{
                          background: m.asset_count > 0 ? '#DBEAFE' : '#F3F4F6',
                          fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                        }}>
                          <Package size={12} /> {m.asset_count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'clients' && (
            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                Add, import, or unlink clients from Product Management → this product → Clients.
              </p>
              {clientsLoading ? (
                <SkeletonBlock />
              ) : clients.length === 0 ? (
                <EmptyState icon={Buildings} title="No clients yet" description="Add client accounts from Product Management → this product → Clients." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {clients.map((c) => (
                    <div key={c.client_id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: 'white',
                    }}>
                      <button type="button"
                        onClick={() => navigate(`/dashboard/assets/teams/${teamId}/clients/${c.client_id}`)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', flex: 1,
                          fontSize: '13px', fontWeight: '600', color: '#002FA7',
                        }}
                        data-testid="client-row">
                        {c.name} {!c.active && <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '400' }}>inactive</span>}
                      </button>
                      <RowActionsMenu actions={[
                        { label: 'Edit', onClick: () => openClientModal(c) },
                        { label: 'Delete', onClick: () => removeClient(c), danger: true },
                      ]} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {clientModal && (
        <ModalShell title="Edit Client" onClose={() => setClientModal(null)} testid="client-modal">
          <form onSubmit={saveClient}>
            <Field label="Name *"><input type="text" value={clientForm.name} required className="input-field"
              onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} /></Field>
            {clientError && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '8px' }}>{clientError}</p>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={clientSaving}>{clientSaving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={() => setClientModal(null)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}

export default TeamDetailPage;
